import { z } from 'zod'
import { ingredientSchema, recipeLineSchema } from './costing'

export const CONFIG_CLASS = 'galley.configuration'
export const DOCUMENT_KEY = 'galley.document'
const color = z.string().regex(/^#[\da-f]{6}$/i, 'Ange en färg som #123456')
const number = (min: number, max: number, fallback: number): z.ZodDefault<z.ZodNumber> => z.number().finite().min(min).max(max).default(fallback)
const integer = (min: number, max: number, fallback: number): z.ZodDefault<z.ZodNumber> => z.number().int().min(min).max(max).default(fallback)
export const screenSchema = z.object({
  layout: z.enum(['compact', 'normal', 'large']).default('normal'),
  colorScheme: z.enum(['dark', 'light', 'high-contrast']).default('dark'),
  columns: z.number().int().min(1).max(8).nullable().default(null),
  fontSizeMultiplier: number(50, 250, 100),
  titleOverride: z.string().nullable().default(null),
  fontFamily: z.enum(['system', 'inter', 'roboto', 'helvetica', 'arial', 'georgia', 'times', 'courier']).default('system'),
  bold: z.boolean().default(false),
  italic: z.boolean().default(false),
  displayMode: z.enum(['auto', 'primary', 'secondary', 'both']).default('auto'),
  preparationMode: z.literal('item').default('item'),
  showSlipCompleteButton: z.enum(['default', 'on', 'off']).default('default'),
  showItemCounter: z.boolean().default(false),
  articleCounterOnly: z.boolean().default(false),
  itemCounterWidth: integer(180, 800, 320),
  itemCounterRowSize: number(50, 200, 100),
  itemCounterIncludeAlternatives: z.boolean().default(true),
  secondaryOpacity: number(10, 100, 60),
  pollIntervalSeconds: integer(2, 120, 5),
  hideBumpedAfterSeconds: integer(0, 86400, 0),
  preorderThresholdMinutes: integer(0, 10080, 30),
  autoMarkPickedUpAfterSec: integer(0, 86400, 0),
  keepPaidSlips: z.boolean().default(false),
  keepPaidSlipsAutoExpireMin: integer(1, 1440, 60),
  slipFullHeight: z.boolean().default(true),
  slipMaxHeight: integer(150, 2000, 600),
  treatAllAsPrimary: z.boolean().default(false),
  soundOnNewOrder: z.boolean().default(true),
  soundVolume: number(0, 100, 70),
  showOnlyChannels: z.array(z.string()).default([]),
  automaticallyPrintNewSlips: z.boolean().default(false),
})
const mapping = z.object({ productName: z.string().min(1), ratio: z.number().finite().positive() })
export const counterSchema = z.object({
  enabled: z.boolean().default(false),
  printWorkflowEnabled: z.boolean().default(false),
  excludedProductNames: z.array(z.string()).default([]),
  autoCompleteProductNames: z.array(z.string()).default([]),
  optionMappings: z.record(z.string(), mapping).default({}),
  productMappings: z.record(z.string(), mapping).default({}),
  bundleMappings: z.record(z.string(), z.array(mapping)).default({}),
  comboMappings: z.array(z.object({ productName: z.string(), optionName: z.string(), countAs: z.string(), ratio: z.number().finite().positive() })).default([]),
})
export const orderDisplaySchema = z.object({
  headingPreparing: z.string().default('Tillagas'),
  headingReady: z.string().default('Klar att hämta'),
  backgroundColor: color.default('#101b20'),
  textColor: color.default('#f5f7f8'),
  accentColor: color.default('#32c6a6'),
  readyColor: color.default('#66dd88'),
  logoUrl: z.union([z.literal(''), z.url()]).default(''),
  showLogo: z.boolean().default(false),
  fontFamily: screenSchema.shape.fontFamily,
  numberScale: number(50, 250, 100),
  headingScale: number(50, 250, 100),
  columnsPerSide: integer(1, 8, 3),
  highlightNewlyReadySeconds: integer(0, 300, 30),
  chimeOnReady: z.boolean().default(true),
  chimeVolume: number(0, 100, 70),
  pollIntervalSeconds: integer(2, 120, 5),
  showChannelBadges: z.boolean().default(false),
})
export const buffetScreenSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sections: z.array(z.object({ name: z.string(), columns: integer(1, 12, 4), rows: integer(1, 12, 3) })).min(1),
  presets: z.array(z.object({ name: z.string().min(1), durationMinutes: integer(1, 1440, 60), productId: z.string().nullable().default(null) })).default([]),
  orangeThresholdPercent: number(0, 100, 50),
  redThresholdPercent: number(0, 100, 20),
  hideTimer: z.boolean().default(false),
})
export const labelSchema = z.object({
  enabled: z.boolean().default(false),
  printerQueueId: z.string().nullable().default(null),
  widthMm: number(20, 120, 58),
  heightMm: number(20, 150, 40),
  channels: z.array(z.string()).default(['TakeAway', 'Delivery', 'Wolt', 'UberEats', 'DeliveryHero']),
  productIds: z.array(z.string()).default([]),
  alwaysPrintProductIds: z.array(z.string()).default([]),
  stationPrinters: z.record(z.string(), z.string()).default({}),
})
export const configurationSchema = z.object({
  version: z.literal(1).default(1),
  screenDefaults: screenSchema.default(() => screenSchema.parse({})),
  screens: z.record(z.string(), screenSchema).default({}),
  tillColors: z.array(z.object({ tillId: z.string(), color, label: z.string().nullable().default(null) })).default([]),
  counter: counterSchema.default(() => counterSchema.parse({})),
  orderDisplay: orderDisplaySchema.default(() => orderDisplaySchema.parse({})),
  buffetScreens: z.array(buffetScreenSchema).default([]),
  labels: labelSchema.default(() => labelSchema.parse({})),
  ingredients: z.array(ingredientSchema).default([]),
  recipes: z.record(z.string(), z.array(recipeLineSchema)).default({}),
  wasteEnabled: z.boolean().default(false),
  ticketTimeEnabled: z.boolean().default(true),
})
export type ScreenSettings = z.infer<typeof screenSchema>
export type Configuration = z.infer<typeof configurationSchema>
export const DEFAULT_CONFIGURATION = configurationSchema.parse({})
export function readConfiguration(value: unknown): Configuration {
  return value == null ? DEFAULT_CONFIGURATION : configurationSchema.parse(value)
}
export function screenSettings(config: Configuration, id: string): ScreenSettings {
  return config.screens[id] ?? config.screenDefaults
}
